import type { SystemRole, SystemRoleDraft } from '@clockwise/clients/system-roles';
import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { systemRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { CatalogFormSheet } from './catalog-form-sheet';

/**
 * Duplicate a system role: fetch what a clone would be (a unique
 * "<name> (copy)", the source's description and permissions — the server
 * decides, see `GET /api/system-roles/:id/clone`), open the ordinary Add sheet
 * with it, and create through `POST /api/system-roles/:id/clone` with whatever
 * the user changed.
 *
 * Shared by the role list and the role detail page; render `sheet` once and
 * call `start(id)` from a button. `onCreated` runs after the copy exists (the
 * detail page uses it to open the copy).
 */
export function useDuplicateSystemRole(onCreated?: (role: SystemRole) => void): {
  /** Stable across renders, so a caller's memoised table columns stay memoised. */
  start: (sourceId: string) => Promise<void>;
  pending: boolean;
  sheet: ReactNode;
} {
  const [source, setSource] = useState<{ id: string; draft: SystemRoleDraft } | null>(null);

  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  // A read, so it goes through the query client under its own key; staleTime 0
  // because the free copy name changes as roles are added.
  const start = useCallback(
    async (id: string) => {
      setPending(true);
      try {
        const res = await queryClient.fetchQuery({
          queryKey: queryKeys.systemRoles.cloneDraft(id),
          queryFn: () => systemRolesApi.cloneDraft(id),
          staleTime: 0,
        });
        setSource({ id, draft: res.data });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not prepare the copy');
      } finally {
        setPending(false);
      }
    },
    [queryClient],
  );

  const sheet = (
    <CatalogFormSheet
      open={source !== null}
      onOpenChange={(open) => {
        if (!open) setSource(null);
      }}
      entityLabel="system role"
      entity={null}
      initial={source?.draft ?? null}
      permissionsEditable
      uniqueWithin="its Business Unit"
      // Shown, fixed: the copy is made in its source's Business Unit.
      unitField={{
        current: source?.draft.businessUnitId,
        view: 'system_role.view',
        add: 'system_role.edit',
      }}
      submit={async (body) => {
        if (!source) throw new Error('Duplicate: no source role');
        const created = await systemRolesApi.clone(source.id, {
          name: body.name,
          description: body.description,
          permissions: body.permissions ?? [],
        });
        onCreated?.(created.data);
        return created;
      }}
      invalidateKeys={[queryKeys.systemRoles.all, queryKeys.audit.all]}
    />
  );

  return { start, pending, sheet };
}
